import React, { useState, useEffect, useRef } from "react"
import { useDispatch } from "react-redux"
import { setPageTitle } from "../../../redux/store/themeConfigSlice"
import { useNavigate } from "react-router-dom"
import * as XLSX from "xlsx"
import { TAgetCampaignApplicationTime } from "../../../services/campaignsAPI"
import CampaignStatsHeader from "./components/CampaignStatsHeader"
import CampaignStatsSummaryCards from "./components/CampaignStatsSummaryCards"
import CampaignTransitionsTable from "./components/CampaignTransitionsTable"

interface TransitionData {
  from: string
  to: string
  sum: number
  count: number
  avgMs: number
}

interface PeriodData {
  period: string
  transitions: TransitionData[]
  totals: {
    totalApplicationsNew: number
    totalApplicationsDoneNew: number
  }
}

interface CampaignStatsResponse {
  params: {
    start: string
    end: string
    groupBy: string
    timezone: string
    campaigns?: string[]
  }
  data: PeriodData[]
}

const CampaignStats: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const abortControllerRef = useRef<AbortController | null>(null)
  const [statsData, setStatsData] = useState<CampaignStatsResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedDays, setSelectedDays] = useState<number>(7)
  const [startDate, setStartDate] = useState<string>(
    new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
  )
  const [endDate, setEndDate] = useState<string>(new Date().toISOString().split("T")[0])
  const [groupBy] = useState<"day" | "week" | "month">("day")
  const [viewMode, setViewMode] = useState<"day" | "week" | "month">("day")

  useEffect(() => {
    dispatch(setPageTitle("Campaign Statistics"))
    fetchCampaignApplicationTime()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const fetchCampaignApplicationTime = async () => {
    await fetchCampaignApplicationTimeWithDates(startDate, endDate)
  }

  const fetchCampaignApplicationTimeWithDates = async (
    start: string,
    end: string,
    groupByParam?: "day" | "week" | "month",
  ) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    abortControllerRef.current = new AbortController()

    setLoading(true)
    setError(null)
    try {
      const data = await TAgetCampaignApplicationTime(
        start,
        end,
        groupByParam || groupBy,
        undefined,
        abortControllerRef.current.signal,
      )
      setStatsData(data)
    } catch (err: unknown) {
      const error = err as {
        name?: string
        code?: string
        response?: { status: number }
        message?: string
      }
      if (err instanceof Error && (err.name === "AbortError" || error.code === "ERR_CANCELED")) {
        setLoading(false)
        return
      }

      if (error.response && error.response.status === 401) {
        navigate("/login")
      }
      setError(error.message || "Kampanya istatistikleri alınırken bir hata oluştu.")
    } finally {
      setLoading(false)
    }
  }

  const handleQuickSelect = async (days: number) => {
    const endDateObj = new Date()
    const startDateObj = new Date()
    startDateObj.setDate(endDateObj.getDate() - days)

    const newStartDate = startDateObj.toISOString().split("T")[0]
    const newEndDate = endDateObj.toISOString().split("T")[0]

    setStartDate(newStartDate)
    setEndDate(newEndDate)
    setSelectedDays(days)
    setLoading(true)
    await fetchCampaignApplicationTimeWithDates(newStartDate, newEndDate)
    setLoading(false)
  }

  const handleDateRangeChange = async () => {
    setLoading(true)
    await fetchCampaignApplicationTimeWithDates(startDate, endDate)
    setLoading(false)
  }

  const handleViewModeChange = async (mode: "day" | "week" | "month") => {
    setLoading(true)
    setViewMode(mode)
    await fetchCampaignApplicationTimeWithDates(startDate, endDate, mode)
    setLoading(false)
  }

  const getStateDisplayName = (state: string) => {
    const stateNames: { [key: string]: string } = {
      invite_new: "Davet Edilen Profiller",
      invite_success: "Daveti Kabul Eden Profiller",
      ugc_content: "UGC İçerikler",
      ugc_rejected: "UGC İçerik Reddedildi",
      first_application: "Başvuran Profiller",
      waiting_address: "Adres Beklenen Profiller",
      address_to_approve: "Adres Onayı Bekleniyor",
      waiting_content: "İçerik Bekleniyor",
      content_to_share: "Paylaşılacak İçerik",
      content_approved: "Onaylanan İçerikler",
      content_shared: "Paylaşılan İçerikler",
      application_done: "Başvuru Tamamlandı",
      content_rejected: "İçerik Reddedildi",
      address_rejected: "Adres Reddedildi",
      account_rejected: "Hesap Reddedildi",
      brand_canceled: "Marka Tarafından İptal Edildi",
      user_canceled: "Kullanıcı Tarafından İptal Edildi",
      application_timeout: "Başvuru Zaman Aşımı",
      saved_users: "Kaydedilen Kullanıcılar",
      product_sent: "Ürün Gönderildi",
    }
    return stateNames[state] || state
  }

  const formatDuration = (durationMs: number) => {
    const durationSeconds = durationMs / 1000
    if (durationSeconds < 60) {
      return `${Math.round(durationSeconds)} saniye`
    } else if (durationSeconds < 3600) {
      return `${Math.round(durationSeconds / 60)} dakika`
    } else if (durationSeconds < 86400) {
      return `${Math.round(durationSeconds / 3600)} saat`
    } else {
      return `${Math.round(durationSeconds / 86400)} gün`
    }
  }

  const downloadCSV = () => {
    if (!statsData || statsData.data.length === 0) {
      return
    }

    const csvData = [["Dönem", "Toplam Geçiş Sayısı", "Geçiş Detayları"]]

    statsData.data.forEach((item) => {
      const transitionsText = item.transitions
        .map(
          (t) =>
            `${getStateDisplayName(t.from)} → ${getStateDisplayName(t.to)}: ${formatDuration(
              t.avgMs,
            )} (${t.count} kez)`,
        )
        .join("; ")

      csvData.push([
        item.period,
        item.transitions.reduce((sum, t) => sum + t.count, 0).toString(),
        transitionsText,
      ])
    })

    const csvContent = csvData.map((row) => row.join(",")).join("\n")
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
    const link = document.createElement("a")
    const url = URL.createObjectURL(blob)
    link.setAttribute("href", url)
    link.setAttribute(
      "download",
      `kampanya_başvuru_süreleri_${groupBy}_${new Date().toISOString().split("T")[0]}.csv`,
    )
    link.style.visibility = "hidden"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const downloadExcel = () => {
    if (!statsData || statsData.data.length === 0) {
      return
    }

    const workbook = XLSX.utils.book_new()

    const mainData = [["Dönem", "Toplam Geçiş Sayısı", "Geçiş Detayları"]]

    statsData.data.forEach((item) => {
      const transitionsText = item.transitions
        .map(
          (t) =>
            `${getStateDisplayName(t.from)} → ${getStateDisplayName(t.to)}: ${formatDuration(
              t.avgMs,
            )} (${t.count} kez)`,
        )
        .join("; ")

      mainData.push([
        item.period,
        item.transitions.reduce((sum, t) => sum + t.count, 0).toString(),
        transitionsText,
      ])
    })

    const mainWorksheet = XLSX.utils.aoa_to_sheet(mainData)
    XLSX.utils.book_append_sheet(workbook, mainWorksheet, "Kampanya İstatistikleri")

    const transitionsData = [["Dönem", "Durumdan", "Duruma", "Ortalama Süre", "Geçiş Sayısı"]]

    statsData.data.forEach((item) => {
      item.transitions.forEach((transition) => {
        transitionsData.push([
          item.period,
          getStateDisplayName(transition.from),
          getStateDisplayName(transition.to),
          formatDuration(transition.avgMs),
          transition.count.toString(),
        ])
      })
    })

    const transitionsWorksheet = XLSX.utils.aoa_to_sheet(transitionsData)
    XLSX.utils.book_append_sheet(workbook, transitionsWorksheet, "Geçiş Detayları")

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" })
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    })

    const link = document.createElement("a")
    const url = URL.createObjectURL(blob)
    link.setAttribute("href", url)
    link.setAttribute(
      "download",
      `kampanya_başvuru_süreleri_${groupBy}_${new Date().toISOString().split("T")[0]}.xlsx`,
    )
    link.style.visibility = "hidden"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span>Kampanya istatistikleri yükleniyor...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-600 mb-4">{error}</div>
          <button
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
            onClick={() => fetchCampaignApplicationTime()}
          >
            Tekrar Dene
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <CampaignStatsHeader
        selectedDays={selectedDays}
        startDate={startDate}
        endDate={endDate}
        loading={loading}
        statsData={statsData}
        onQuickSelect={handleQuickSelect}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onDateRangeChange={handleDateRangeChange}
        onRefresh={() => fetchCampaignApplicationTime()}
        onDownloadCSV={downloadCSV}
        onDownloadExcel={downloadExcel}
      />

      {statsData && (
        <>
          <CampaignStatsSummaryCards data={statsData.data} />

          <CampaignTransitionsTable
            data={statsData.data}
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
          />
        </>
      )}

      {/* Empty State */}
      {(!statsData || statsData.data.length === 0) && !loading && (
        <div className="text-center py-16">
          <div className="bg-white rounded-xl shadow-lg p-12 border border-gray-200">
            <div className="text-gray-400 text-8xl mb-6">📊</div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Henüz veri yok</h3>
            <p className="text-lg text-gray-600 mb-6">
              Seçilen zaman aralığında kampanya başvuru süreleri henüz hesaplanmamış.
            </p>
            <button
              onClick={() => fetchCampaignApplicationTime()}
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
            >
              Verileri Yenile
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default CampaignStats
